import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  type DragEndEvent,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortableSensors } from "@/lib/dnd-sensors";
import { Plus } from "lucide-react";
import {
  TIER_PROGRAM_ROW_KINDS,
  TIER_PROGRAM_ROW_KIND_LABEL,
  type Tier,
  type TierProgramCell,
  type TierProgramDetail,
  type TierProgramMatrixRow,
  type TierProgramRowKind,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  aggregateSaveStatus,
  type SaveStatus,
} from "@/hooks/use-autosave";
import { AddMatrixRowDialog } from "./add-matrix-row-dialog";
import { AddTierDialog } from "./add-tier-dialog";
import { TierColumnHeader } from "./tier-column-header";
import { TierMatrixRow } from "./tier-matrix-row";
import { useReorderTiers } from "./use-reorder-tiers";
import { useReorderTierRows } from "./use-reorder-rows";

// Phase 7 sub-bucket 12 — the matrix editor.
//
// Layout (mirrors legacy TierMatrixEditor.tsx with column drag-reorder added):
//   ┌────────┬────────┬────────┬─────┐
//   │ Bronze │ Silver │  Gold  │ +   │   ← column header strip (sortable)
//   └────────┴────────┴────────┴─────┘
//   Requirements        +Add requirement
//   ┌────────────────────┬────────┬────────┬────────┬───┐
//   │ Row 1              │  Yes   │  Yes   │  Yes   │ × │
//   │ Row 2              │  200k  │  500k  │  2M    │ × │
//   └────────────────────┴────────┴────────┴────────┴───┘
//   Benefits            +Add benefit
//   ┌────────────────────┬────────┬────────┬────────┬───┐
//   │ Discount %         │  5%    │  10%   │  15%   │ × │
//   └────────────────────┴────────┴────────┴────────┴───┘
//
// Three sibling DndContexts — one for tier columns, one per row kind. Forms
// builder (sub-bucket 11) proves the sibling pattern; nested DndContexts buy
// nothing because column UUIDs and row UUIDs never collide and the drag axis
// differs (horizontal vs vertical).
//
// Save status aggregation: each TierMatrixRow runs its own useAutosave for
// the row label. Rows report status via a callback into a Map; the editor
// aggregates and re-reports to the page header. Per-cell on-blur writes and
// per-tier-name inline edits are not counted — they resolve via their own
// mutation.isPending feedback.
//
// Drag-handle disable during pending reorder mutations is the slow-3G
// mitigation (whole-tree PATCH serializes via cancelQueries + onSettled
// invalidate; disabling the handle prevents rapid drag-drop-drag drift).

export function TierMatrixEditor({
  detail,
  onSaveStatusChange,
}: {
  detail: TierProgramDetail;
  onSaveStatusChange: (status: SaveStatus) => void;
}) {
  const { program, tiers, rows, cells } = detail;
  const [addTierOpen, setAddTierOpen] = useState(false);
  const [addRowOpen, setAddRowOpen] = useState<TierProgramRowKind | null>(null);
  const [rowStatuses, setRowStatuses] = useState<Record<string, SaveStatus>>(
    {},
  );

  const reorderTiers = useReorderTiers();
  const reorderRows = useReorderTierRows();

  const handleRowStatusChange = useCallback(
    (rowId: string, status: SaveStatus) => {
      setRowStatuses((prev) =>
        prev[rowId] === status ? prev : { ...prev, [rowId]: status },
      );
    },
    [],
  );

  // Aggregate across all live rows. Stale entries from deleted rows are
  // purged when the underlying rows list changes (effect below).
  const aggregateStatus = useMemo(
    () => aggregateSaveStatus(...Object.values(rowStatuses)),
    [rowStatuses],
  );

  useEffect(() => {
    onSaveStatusChange(aggregateStatus);
  }, [aggregateStatus, onSaveStatusChange]);

  // Prune statuses for rows that no longer exist (after delete or reorder).
  useEffect(() => {
    setRowStatuses((prev) => {
      const liveIds = new Set(rows.map((r) => r.id));
      const next: Record<string, SaveStatus> = {};
      let changed = false;
      for (const [id, status] of Object.entries(prev)) {
        if (liveIds.has(id)) {
          next[id] = status;
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [rows]);

  // Cell lookup keyed by `${rowId}:${tierId}` — drives O(1) cell fetches in
  // each row component without re-scanning the cells array per cell render.
  const cellMap = useMemo(() => {
    const m = new Map<string, TierProgramCell>();
    for (const c of cells) m.set(`${c.row_id}:${c.tier_id}`, c);
    return m;
  }, [cells]);

  const rowsByKind = useCallback(
    (kind: TierProgramRowKind): TierProgramMatrixRow[] =>
      rows
        .filter((r) => r.row_kind === kind)
        .sort((a, b) => a.position - b.position),
    [rows],
  );

  const sensors = useSortableSensors();

  const tierIds = useMemo(() => tiers.map((t) => t.id), [tiers]);

  function onTierDragEnd(e: DragEndEvent) {
    const activeId = e.active.id as string;
    const overId = e.over?.id as string | undefined;
    if (!overId || activeId === overId) return;
    const fromIdx = tierIds.indexOf(activeId);
    const toIdx = tierIds.indexOf(overId);
    if (fromIdx < 0 || toIdx < 0) return;
    const orderedIds = arrayMove(tierIds, fromIdx, toIdx);
    reorderTiers.mutate({ programId: program.id, orderedIds });
  }

  function onRowDragEnd(kind: TierProgramRowKind) {
    return (e: DragEndEvent) => {
      const activeId = e.active.id as string;
      const overId = e.over?.id as string | undefined;
      if (!overId || activeId === overId) return;
      const kindRows = rowsByKind(kind);
      const ids = kindRows.map((r) => r.id);
      const fromIdx = ids.indexOf(activeId);
      const toIdx = ids.indexOf(overId);
      if (fromIdx < 0 || toIdx < 0) return;
      const orderedIds = arrayMove(ids, fromIdx, toIdx);
      reorderRows.mutate({ programId: program.id, kind, orderedIds });
    };
  }

  return (
    <div className="space-y-8">
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold tracking-tight">
            Tiers
          </h2>
          <Button type="button" size="sm" onClick={() => setAddTierOpen(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add tier
          </Button>
        </div>
        {tiers.length === 0 ? (
          <div className="rounded-xl border border-dashed bg-card/50 p-6 text-center text-sm text-muted-foreground">
            No tiers yet. Add a tier to start building the matrix.
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onTierDragEnd}
          >
            <SortableContext
              items={tierIds}
              strategy={horizontalListSortingStrategy}
            >
              <div className="flex items-start gap-3 overflow-x-auto pb-1">
                {tiers.map((tier) => (
                  <TierColumnHeader
                    key={tier.id}
                    tier={tier}
                    programId={program.id}
                    canDrag={!reorderTiers.isPending}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </section>

      {TIER_PROGRAM_ROW_KINDS.map((kind) => {
        const kindRows = rowsByKind(kind);
        const kindLabelLower = TIER_PROGRAM_ROW_KIND_LABEL[kind].toLowerCase();
        return (
          <section key={kind}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold tracking-tight">
                {TIER_PROGRAM_ROW_KIND_LABEL[kind]}s
              </h2>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setAddRowOpen(kind)}
                disabled={tiers.length === 0}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Add {kindLabelLower}
              </Button>
            </div>

            {kindRows.length === 0 ? (
              <div className="rounded-xl border border-dashed bg-card/50 p-6 text-center text-sm text-muted-foreground">
                No {kindLabelLower}s yet.
                {tiers.length === 0 && (
                  <span className="mt-1 block">Add at least one tier first.</span>
                )}
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onRowDragEnd(kind)}
              >
                <RowSection
                  programId={program.id}
                  tiers={tiers}
                  rows={kindRows}
                  cellMap={cellMap}
                  reorderPending={reorderRows.isPending}
                  onRowStatusChange={handleRowStatusChange}
                />
              </DndContext>
            )}
          </section>
        );
      })}

      <AddTierDialog
        programId={program.id}
        open={addTierOpen}
        onOpenChange={setAddTierOpen}
      />
      {addRowOpen !== null && (
        <AddMatrixRowDialog
          programId={program.id}
          kind={addRowOpen}
          open={true}
          onOpenChange={(o) => {
            if (!o) setAddRowOpen(null);
          }}
        />
      )}
    </div>
  );
}

function RowSection({
  programId,
  tiers,
  rows,
  cellMap,
  reorderPending,
  onRowStatusChange,
}: {
  programId: string;
  tiers: Tier[];
  rows: TierProgramMatrixRow[];
  cellMap: Map<string, TierProgramCell>;
  reorderPending: boolean;
  onRowStatusChange: (rowId: string, status: SaveStatus) => void;
}) {
  // Sticky 220px label column + N tier cells + auto-width delete column.
  const gridTemplate = `minmax(220px, 1fr) repeat(${tiers.length}, minmax(140px, 1fr)) auto`;
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <div
        className="grid divide-x border-b bg-muted/30 text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        <div className="px-3 py-2">Row</div>
        {tiers.map((t) => (
          <div key={t.id} className="truncate px-3 py-2 text-center">
            {t.name}
          </div>
        ))}
        <div className="w-10" />
      </div>
      <SortableContext
        items={rows.map((r) => r.id)}
        strategy={verticalListSortingStrategy}
      >
        {rows.map((row) => (
          <TierMatrixRow
            key={row.id}
            programId={programId}
            row={row}
            tiers={tiers}
            cellMap={cellMap}
            canDrag={!reorderPending}
            gridTemplate={gridTemplate}
            onSaveStatusChange={(s) => onRowStatusChange(row.id, s)}
          />
        ))}
      </SortableContext>
    </div>
  );
}

